import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client';
import { TaskUpdatedNotification } from 'types/pushNotifications';
import { splitPascalCase } from 'utils/stringUtils';
import { getExchangeTaskFamily, getExchangeTaskTitle } from './exchangeTaskIdentity';

export interface TaskIdentity {
  id?: string;
}

type TaskIdentitySource = Pick<AutopilotTaskDto, 'id'>;

const toDomIdPart = (value?: string) => value?.trim().replace(/[^A-Za-z0-9_-]+/g, '-') || 'none';

export const getTaskIdentity = (task?: TaskIdentitySource): TaskIdentity => ({
  id: task?.id,
});

export const getTaskDomId = (prefix: string, task?: TaskIdentitySource) => {
  const { id } = getTaskIdentity(task);

  return [prefix, id].map(toDomIdPart).join('-');
};

const taskTypeLabels: Partial<Record<TaskCategoryTaskType, string>> = {
  [TaskCategoryTaskType.NswWithdrawnPayments]: 'NSW Payments (Withdrawn)',
  [TaskCategoryTaskType.NswSettlementPayments]: 'NSW Payments (Settlement)',
  [TaskCategoryTaskType.NswTerminationPayments]: 'NSW Payments (Termination)',
  [TaskCategoryTaskType.NswEarlyReleaseOfDepositPayments]: 'NSW Payments (Early Release)',
  [TaskCategoryTaskType.CreateBuyerContactInCrm]: 'Create Buyer Contact in CRM',
  [TaskCategoryTaskType.DepositExceeds10Percent]: 'Deposit exceeds 10%',
  [TaskCategoryTaskType.DepositExceeds10PercentPaymentReview]: 'Deposit exceeds 10% Payment Review',
  [TaskCategoryTaskType.EarlyReleaseOfDepositPaymentReview]:
    'Early Release of Deposit Payment Review',
  [TaskCategoryTaskType.TenancyPreparationAgencyManaged]: 'Request Tenancy Information',
  [TaskCategoryTaskType.TenancyPreparationSelfManaged]: 'Request Tenancy Information',
  [TaskCategoryTaskType.VpaSpreadsheet]: 'VPA Spreadsheet',
  [TaskCategoryTaskType.CampaignVendorSolicitorOutstanding]: 'Vendor Solicitor Outstanding',
  [TaskCategoryTaskType.ContractVendorSolicitorOutstanding]: 'Vendor Solicitor Outstanding',
  [TaskCategoryTaskType.ContractBuyerSolicitorOutstanding]: 'Buyer Solicitor Outstanding',
};

const taskStageLabels: Partial<Record<TaskCategoryStage, string>> = {
  [TaskCategoryStage.IndependentCampaign]: 'Campaign - Other',
  [TaskCategoryStage.IndependentContract]: 'Contract - Other',
};

const dashboardTaskTypeLabels: Partial<Record<TaskCategoryTaskType, string>> = {
  [TaskCategoryTaskType.InitialDeposit]: 'Initial Deposit Due',
  [TaskCategoryTaskType.BalanceDeposit]: 'Balance Deposit Due',
};

export const getTaskTypeLabel = (taskType?: TaskCategoryTaskType) =>
  taskType ? (taskTypeLabels[taskType] ?? splitPascalCase(taskType)) : '';

export const getTaskStageLabel = (stage?: string) =>
  stage ? (taskStageLabels[stage as TaskCategoryStage] ?? splitPascalCase(stage)) : '';

export const getTaskLabel = (task: AutopilotTaskDto) =>
  task.displayTitle ?? getTaskTypeLabel(task.category?.taskType);

export const getTaskDashboardLabel = (task: AutopilotTaskDto) => {
  const { category } = task;
  const exchangeFamily = getExchangeTaskFamily(category?.taskType);

  if (exchangeFamily) {
    return getExchangeTaskTitle(exchangeFamily);
  }

  if (
    category?.stage === TaskCategoryStage.AgreementCompliance ||
    category?.stage === TaskCategoryStage.ContractCompliance
  ) {
    return splitPascalCase(category.stage);
  }

  return category?.taskType
    ? (task.displayTitle ??
        dashboardTaskTypeLabels[category.taskType] ??
        getTaskTypeLabel(category.taskType))
    : '';
};

export const getTaskUpdateIdentity = (taskUpdate?: TaskUpdatedNotification): TaskIdentity =>
  getTaskIdentity(taskUpdate?.task);

export const areTaskIdentitiesEqual = (left?: TaskIdentity, right?: TaskIdentity): boolean =>
  left?.id !== undefined && left.id === right?.id;

export const isSameTask = (task: TaskIdentitySource, candidate: TaskIdentitySource) =>
  areTaskIdentitiesEqual(getTaskIdentity(task), getTaskIdentity(candidate));

export const doesTaskMatchUpdate = (
  task: TaskIdentitySource,
  taskUpdate: TaskUpdatedNotification
) => areTaskIdentitiesEqual(getTaskIdentity(task), getTaskUpdateIdentity(taskUpdate));
